import { CustomElement, OverrideCustomElementConfig } from "@cervval.js/client-html5-core/src/components/customElement.decorator";
import { Event, EventHandler, NoBubblesEventInit } from "@cervval.js/client-html5-core/src/components/event.decorator";
import { TypedHtmlElements } from "@cervval.js/client-html5-core/src/components/typedHtmlElements.decorator";
import { TypedWebComponentOptions, WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { WebComponent } from "@cervval.js/client-html5-core/src/components/webComponent";
import { Display, toggleVisible } from "@cervval.js/client-html5-core/src/tools/css.tools";
import { FLEX_CONTAINER, FLEX_CONTAINER_ROW, FLEX_ITEM, FLEX_ITEM_AUTO, MARGIN } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { createHtmlElement, empty, setHTMLElementAttributes, setHTMLElementContent } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { LOG_SERVICE, LogService, VALIDATION_SERVICE, ValidationService } from "@cervval.js/common/services";
import { Injected } from "@cervval.js/common/src/dependencies/injected";
import { DataModel } from "@cervval.js/common/src/forms/dataModel";
import { DefaultDataSourceContext } from "@cervval.js/common/src/forms/dataSource";
import { PropertyConfig, PropertyLabelLayout, propertyConfigComparator } from "@cervval.js/common/src/forms/propertyConfig";
import { ReferencePropertyConfigTypes } from "@cervval.js/common/src/forms/propertyConfig.types";
import { ValidationError } from "@cervval.js/common/src/forms/validationError";
import { getLogConsole } from "@cervval.js/common/src/services/log.service";
import { requiredValidatorName } from "@cervval.js/common/src/services/validation.impl/validators/required.validator";
import { EDITED_DATA_IDS } from "@cervval.js/common/src/services/validation.impl/validators/validator";
import { anyObject, getProp, setProp } from "@cervval.js/common/src/tools/any.tools";
import { addToArrayRecord, buildArrayRecordWithFunc, getDuplicates, hasDuplicates, removeElementsFromArray, wrapAsArray } from "@cervval.js/common/src/tools/collection.tools";
import { isNumeric, toPx } from "@cervval.js/common/src/tools/number.tools";
import { clone, getVOFValue, isEmptyObject, objectValues } from "@cervval.js/common/src/tools/object.tools";
import { ObjectTools, anyReallyAny } from "@cervval.js/common/tools";
import { CjsEditor } from "../../editors/editor";
import { EDITOR_FACTORY_SERVICE, EditorFactoryService, NewEditorOptions } from "../../editors/editorFactory.service";
import { CjsDateIntervalCalendarEditor } from "../../editors/editorsWithPicker/dateIntervalCalendarEditor/dateIntervalCalendarEditor";
import { EditorMapConfig, FormFactory, FormItem } from "../../editors/factories/formFactory";
import { CjsReferenceListEditor } from "../../editors/referenceListEditor/referenceListEditor";
import { CjsAccordion, defaultAccordionOptions } from "../accordion/accordion";
import { showErrorInDialog } from "../dialog/dialog";
import { EditListEvents, EditListOnDataChangeEvent } from "../editList/editList.events";
import { CjsInput } from "../inputs/input";
import { CjsInputWithButton } from "../inputs/inputWithButton/inputWithButton";
import { CjsTabs, TabElement } from "../tabs/tabs";
import { GeneratedFormEditMode, GeneratedFormEditorOnValueChangeEvent, GeneratedFormMode, GeneratedFormOptions, GeneratedFormRenderFormGridItem, GeneratedFormSaveAction, GeneratedFormSaveMode, GeneratedFormSaveResult } from "./generatedForm.types";

export const VISUAL_RETURN_DELAY = 2000;

/** Uid used to render generatedForm */
const ROW_UID = "@row";

/**
 * List of items html element with an accordion
 */
interface AccordionItemGeneration {

    /** Uid of accordion */
    uid: string;

    /** Accordion component */
    accordion: CjsAccordion;

    /** List of propertyConfigs for this accordion content */
    propertyConfigs: PropertyConfig[];

    /** Parent of accordion, used for nesting */
    parent?: CjsAccordion;
}

/**
 * Map of items html element with an accordion by display order 
 */
type AccordionItems = { [displayOrder: number]: AccordionItemGeneration };


/**
 * List of items html element to generate tabs
 */
interface TabsItemGeneration {

    /** Uid which is the id of div content to show when click on tab */
    uid: string;

    /** Tab display name */
    displayName: string;

    /** List of propertyConfigs for this tabContent */
    propertyConfigs: PropertyConfig[];
}

/**
 * Map of items html element to generate tabs by display order
 */
type TabsItems = { [displayOrder: number]: TabsItemGeneration };

/**
 * Suffix of accordion id which will be concatenate with display order number (eg: id="accordion_0")
 */
export const ACCORDION_SUFFIX_ID = "accordion";

/** Form items css class when form layout is multi-column */
export const ROW_FORM_ITEM = "row-form-item";

/**
 * Form mode by default
 */
const DEFAULT_GENERATED_FORM_MODE: GeneratedFormMode = {
    editMode: GeneratedFormEditMode.UPDATE,
    saveMode: GeneratedFormSaveMode.DYNAMIC
};

/**
 * This mode is forbidden. The default one will be used instead.
 */
const FORBIDDEN_GENERATED_FORM_MODE: GeneratedFormMode = {
    editMode: GeneratedFormEditMode.CREATE,
    saveMode: GeneratedFormSaveMode.DYNAMIC
};

const defaultGeneratedFormOptions: GeneratedFormOptions = {
    formMode: DEFAULT_GENERATED_FORM_MODE,
    disableHideOnEmptyEditedData: false
};

/**
 * Generate a form based on a list of propertyConfig
 * Should be associated to a DataModel, from where value will be edited in form
 */
@CustomElement({
    name: "cjs-generated-form",
    style: require("./generatedForm.scss"),
    template: require("./generatedForm.html"),
    elementCssClasses: [FLEX_CONTAINER_ROW, FLEX_ITEM_AUTO]
})
export class CjsGeneratedForm<DataModelType> extends WebComponent {

    @Injected(LOG_SERVICE, { optional: true })
    protected readonly _log?: LogService;

    @Injected(EDITOR_FACTORY_SERVICE)
    protected _editorFactory!: EditorFactoryService;

    /** Validation */
    @Injected(VALIDATION_SERVICE)
    protected _validation!: ValidationService;

    @TypedHtmlElements()
    public readonly components!: {
        generatedFormContainer: HTMLDivElement
    };

    /** 
     * Event key for on dataModel value is saved 
     */
    @Event(NoBubblesEventInit)
    public dataModelValueSaveEvent!: EventHandler;

    /** 
     * Triggered when a editor value has changed 
     */
    @Event(NoBubblesEventInit)
    public editorValueChangeEvent!: EventHandler<GeneratedFormEditorOnValueChangeEvent>;

    /** Attributes options of GeneratedForm */
    @TypedWebComponentOptions(defaultGeneratedFormOptions)
    public readonly options!: WebComponentOptions<GeneratedFormOptions<DataModelType>>;

    /**
     * Factory used to build formsItems
     */
    protected _factory: FormFactory = new FormFactory();

    /** Properties configs used to generate formItems */
    protected _propertyConfigs: PropertyConfig[] | undefined;

    /** Get propertyConfigs of form */
    public get propertyConfigs(): PropertyConfig[] {
        return this._propertyConfigs ?? [];
    }

    /** Get data source context of form */
    public get dataSourceContext(): DefaultDataSourceContext<DataModelType> {
        return {
            editedData: () => this.getEditedData(),
            formValues: () => this.getFormValues()
        };
    }

    /**
     * Data model of forms
     * Contains the data to edit value
     */
    protected _dataModel: DataModel<DataModelType> | undefined;

    /** Get datamodel of form */
    public get dataModel(): DataModel<DataModelType> | undefined {
        return this._dataModel;
    }

    /**
     * Current data to edit values
     * Should be as the same type than data used in dataModel
     */
    protected _editedData: DataModelType[] = [];

    /**
     * Form items generated displayed in component record by property uid (eg propertyName or objectType.propertyName)
     */
    protected _formItems: Record<string, FormItem> = {};

    /**
     * Tabs used to render form in different sections
     */
    protected _tabs: CjsTabs | undefined;

    /**
     * Accordions used to render form in different sections
     */
    protected _accordions: CjsAccordion[] = [];

    /**
     * Matrix of generatedForm editors render
     * Compute their row, column and colSpan
     */
    protected _renderMatrix: Record<string, GeneratedFormRenderFormGridItem[]> = {};

    /**
     * Check if colSpan is given in propertyConfigs
     * Use to change label display
     */
    protected _colSpanInPropertyConfigs = false;

    /**
     * Save data action in cache,
     * All saveActions will be executed when save() method is called
     * SaveActions are locally cached (in CjsGeneratedForm)
     */
    protected _saveCacheActions: GeneratedFormSaveAction<DataModelType>[] = [];

    /** Map listing the states (boolean => collapsed or opened) of the sections (accordion) of the editlist */
    public accordionCollapsedStateMap: Map<string, boolean> = new Map();

    /** @returns true if generatedForm is disabled */
    public get disabled(): boolean {
        return Boolean(getVOFValue(this.options.disabled));
    }

    constructor(options?: Partial<GeneratedFormOptions<DataModelType>>, customElementOptions?: OverrideCustomElementConfig) {
        super(customElementOptions);
        this.options.setWithoutRefresh(options);

        this.addEventListener(EditListEvents.EDITED_DATA_CHANGED, (e) => {
            const event = e as CustomEvent<EditListOnDataChangeEvent>;
            this.setEditedData(event.detail.editedData);
        });
    }

    /**
     * When generatedForm is created
     */
    public override init(): void {
        this.components.generatedFormContainer.id = "generatedForm" + this.componentNumber;
    }

    /**
     * Refresh the component with stored values
     */
    public override refresh(): void {
        const debugTrace = this.debug.traceStart("refresh");
        // Render if propertyConfigs are set
        if (this._propertyConfigs && this._dataModel) {
            this._renderFormItems(this._propertyConfigs);

            this._refreshOptions();
        }
        this.debug.traceFinish(debugTrace);
    }

    /**
     * Initialize the generated form.
     * To work properly, propertyConfig and dataModel should be set
     * 
     * @param propertyConfigs propertyConfig of GeneratedForm, used to generate forms an their editors
     * @param dataModel dataModel managed by GeneratedForm
     * @param editedData data in edition when component is initialized
     * @param editorMapConfig editorMapping from propertyConfigs, used to set Editor for a given PropertyConfigType
     */
    public initialize(propertyConfigs: PropertyConfig[], dataModel: DataModel<DataModelType>, editedData: DataModelType[], editorMapConfig?: EditorMapConfig): void {

        // Set values
        this._propertyConfigs = propertyConfigs;
        this._dataModel = dataModel;
        this._editedData = editedData;

        if (editorMapConfig) {
            this.setFactoryEditorMapConfig(editorMapConfig);
        }

        // If init was not already called, refresh will be executed later (when init() will be called)
        if (this.isInitDone) {
            this.refresh();
        }
    }

    /** Set editorMap configuration, to retrieve Editor from a PropertyConfig */
    public setFactoryEditorMapConfig(config: EditorMapConfig): void {
        this._factory.setEditorMapConfig(config);
    }

    /** Update edited data */
    public setEditedData(editedData: DataModelType[]): void {
        this._editedData = editedData;

        if (Object.keys(this._formItems).length === 0) {
            // init formItems
            this.refresh();
        } else {
            this._refreshOptions();

            // Update form
            this._updateFormItemsValues(this._editedData);
        }
    }

    /** get edited data */
    public getEditedData(): DataModelType[] {
        return this._editedData;
    }

    /**
     * Returns a copy of data that is going to be saved
     * @returns
     */
    public async getDataToSave(): Promise<DataModelType[]> {
        if (this.options.formMode.editMode === GeneratedFormEditMode.CREATE) {
            return this.getDataToCreate();
        } else if (this._dataModel) {
            // For all saveActionsCached, set the value in dataModel
            const clonedDataModel = new DataModel<DataModelType>();
            clonedDataModel.setData(this._dataModel.getDataCopy().map(data => clone(data)));

            await Promise.all(this._saveCacheActions.map(async (cacheAction: GeneratedFormSaveAction<DataModelType>) => {
                await clonedDataModel.setPropertyValue(cacheAction.propertyName, cacheAction.data, cacheAction.value);
            }));

            const originalData = this._dataModel.getDataCopy();
            const modifiedData = clonedDataModel.getDataCopy();
            const differenceData: DataModelType[] = [];
            for (let index = 0; index < originalData.length; index++) {
                const originalElement = originalData[index];
                const modifiedElement = modifiedData[index];
                if (!ObjectTools.deepEqual(originalElement, modifiedElement)) {
                    differenceData.push(modifiedElement);
                }

            }
            return differenceData;
        } else {
            return [];
        }
    }

    /**
     * Save element in the form in action or in DataModel
     * Value priority : Item > form > pc default > null
     * @param saveInDataModel if true, the save method will do the modification in the Data Model
     * @returns {SaveResult<DataModelType>} Output useful for action creation
     */
    public save(saveInDataModel = true): GeneratedFormSaveResult<DataModelType> {
        const saveResult: GeneratedFormSaveResult<DataModelType> = { elementsToCreate: [], elementsToUpdate: [] };

        if (this._dataModel) {
            if (this.options.formMode.editMode === GeneratedFormEditMode.CREATE) {
                const dataToCreate = this.getDataToCreate();
                const objs = this.options.finishCreate?.(dataToCreate) ?? dataToCreate;
                saveResult.elementsToCreate.push(...objs);
                if (saveInDataModel) {
                    this._dataModel.addItemOrArray(objs);
                }
                this._dispatchOnDataModelValueSaved();
            } else {
                // For all saveActions cached, set the value in dataModel
                Promise.all(this._saveCacheActions.map(async (cacheAction: GeneratedFormSaveAction<DataModelType>) => {
                    saveResult.elementsToUpdate.push(cacheAction);
                    if (saveInDataModel && this._dataModel) {
                        await this._dataModel.setPropertyValue(cacheAction.propertyName, cacheAction.data, cacheAction.value);
                    }
                })).then(_ => {
                    this._dispatchOnDataModelValueSaved(); // We wait for all modifications to be done before triggering the event
                }).catch(showErrorInDialog);
            }
        }

        // Reset saveCacheActions
        this._saveCacheActions = [];

        return saveResult;
    }

    /**
     * Call validation service on form editors 
     * ! Form items with property config option disabled won't be validate by default. Use the option validateDisabledFormItem to validate it.
     * @param options
     * @param options.propertyUids list of form editors to check for validation, if not given, validate all form editors. If a formItem for one of the propertyUids is disabled, you need to explicitly set options.validateDisabledFormItem if you want validation on it.
     * @param options.validateDisabledFormItems validate form item that have property config with disabled option and editor which is disabled
     * @returns true if form is valid
     */
    public validateForm(options?: { propertyUids?: string[], validateDisabledFormItems?: boolean }): boolean {
        const propsThatCanBeValidated: string[] = [];
        const allProps: Record<string, PropertyConfig> = {};
        const propsValues: Record<string, unknown> = {};
        const oldValues: Record<string, unknown> = {};

        // Store edited data ids, allowing for more complex validation
        const editedDataIds = [];
        const itemsToValidate = this.getUpToDateData(this._editedData);
        for (const data of itemsToValidate) {
            if (data == null) { continue; }
            const id = getProp(data, "id");
            if (id != null && isNumeric(id)) {
                editedDataIds.push(id);
            }
        }
        propsValues[EDITED_DATA_IDS] = editedDataIds;

        for (const propertyUid in this._formItems) {

            const formItem = this._formItems[propertyUid];
            propsValues[propertyUid] = formItem.editor.getValue();

            for (const data in this._editedData) {
                const editedData = getProp<anyObject>(this._editedData, data)[propertyUid];
                oldValues[propertyUid] = editedData;
            }

            allProps[propertyUid] = formItem.propertyConfig;

            // Don't validate if property config has disabled option and if the editor is disabled (can be bypass by using validateDisabledFormItems option)
            if (!options?.validateDisabledFormItems && formItem.propertyConfig.options?.disabled && formItem.editor.disabled) {
                // do not validate
            } else {
                propsThatCanBeValidated.push(propertyUid);
            }
        }
        const propsToValidate = options?.propertyUids ?? propsThatCanBeValidated;
        const errors = this._validation.validate(allProps, propsValues, propsToValidate);

        for (const propertyUid of propsToValidate) {
            const formItem = this._formItems[propertyUid];
            // reset or display only propertyUid requested
            if (options?.propertyUids?.length) {
                if (options?.propertyUids.includes(propertyUid)) {
                    // reset or display validation error on form item's editor
                    formItem.editor.setValidationError(errors[propertyUid]);
                }
            } else {
                // reset or display validation error on form item's editor
                formItem.editor.setValidationError(errors[propertyUid]);
            }

        }

        // Focus first editor wih validation error and change section if needed
        // Only do it in static mode to avoid focusing an editor with validation error when switching item in an edit list for example
        if (!this.options.disableAutoFocusOnValidationError && this.options.formMode.saveMode === GeneratedFormSaveMode.STATIC) {
            this._focusFirstEditorWithValidationError(errors);
        }

        // Test if there are errors (that are not warnings) in Record
        return Object.values(errors).filter(e => !e?.isWarning).length === 0;
    }

    /** Get form items of generated form */
    public getFormItems<K extends string>(): Record<K, FormItem> {
        return this._formItems;
    }

    /**
     * Get form item editor
     * ! Will return undefined if form isn't rendered yet
     */
    public getEditor<T extends CjsEditor<anyReallyAny> = CjsEditor<anyReallyAny>>(propertyName: string): T | undefined {
        return this._formItems[propertyName]?.editor as T | undefined;
    }

    /**
     * set editors values in (or default values in property config) on provided object
     */
    protected _setFormValues(formValues: Partial<DataModelType>): void {
        // Get default values
        if (this._propertyConfigs) {
            for (const pc of this._propertyConfigs) {
                const value = getVOFValue(pc.options?.defaultValue) ?? null;
                setProp(formValues, pc.propertyName, value);
            }
        }

        // Get value from each editors
        for (const item of objectValues(this.getFormItems())) {
            setProp(formValues, item.propertyConfig.propertyName, item.editor.getValue());
        }
    }

    /**
     * Returns an object with editors values in the form (or default values in property config)
     */
    public getFormValues(): Partial<DataModelType> {
        const formValues: Partial<DataModelType> = {};
        this._setFormValues(formValues);
        return formValues;
    }

    /**
     * Returns an object that contains default values from property configs and editors value in the form
     * ! Modifies _editedData and returns only the first editedData
     * Value priority : Editor > pc default > null
     */
    public getDataToCreate(): DataModelType[] {
        const obj = this._editedData[0] ?? {} as DataModelType;
        this._setFormValues(obj);
        return [obj];
    }

    /**
     * Returns clones of edited data with values overridden by form values
     */
    public getUpToDateData(editedData: DataModelType[] = this._editedData): DataModelType[] {
        const formValues = this.getFormValues();
        return editedData.length ? editedData.map(item => ({ ...item, ...formValues })) : [formValues as DataModelType];
    }

    /**
     * Refresh options
     */
    protected _refreshOptions(): void {
        const debugTrace = this.debug.traceStart("_refreshOptions");
        // Refresh and correct form mode if necessary
        this._refreshFormMode();

        // Hide the form container if necessary
        this._refreshHideOnEmptyEditedData();

        if (this.options.isNotEditable) {
            let editedDataIsGloballyNotEditable = false;
            for (const d of this._editedData) {
                if (this.options.isNotEditable(d)) {
                    // if one is not editable => we disabled edition for all
                    editedDataIsGloballyNotEditable = true;
                    break;
                }
            }
            // Apply to all fields
            for (const formItem of Object.values(this.getFormItems())) {
                // A field is disabled :
                // - if edited data has any "globally not editable" object
                // - or if the field is disabled by the property config
                const fieldDisabled = editedDataIsGloballyNotEditable || getVOFValue(formItem.propertyConfig.options?.disabled);
                formItem.editor.options.setAndRefreshIfChanged({ disabled: fieldDisabled });
            }
        }
        this.debug.traceFinish(debugTrace);
    }

    /**
     * Focus the first editor with validation error in the form 
     * In a form with sections, if the validation error is not in the current section, it will switch to the section with validation error
     * @param errors Validation errors
     */
    protected _focusFirstEditorWithValidationError(errors: Record<string, ValidationError | undefined>): void {
        // Array of all properties names with validation error
        const propertyNames = Object.keys(errors);

        // Returns if no errors
        if (!propertyNames.length) { return; }

        // Scroll to first editor in error and focus it
        const scrollAndFocusEditor = (editor: CjsEditor<unknown>): void => {
            editor.focus();
            editor.scrollIntoView({ behavior: "smooth", block: "center" });
        };

        // Handle form without sections
        if (this._tabs == null && !this._accordions.length) {
            scrollAndFocusEditor(this._formItems[propertyNames[0]].editor);
        } else {
            // Sort property names by section display order
            propertyNames.sort((a, b) => {
                const displayOrderA = this._formItems[a].section?.displayOrder;
                const displayOrderB = this._formItems[b].section?.displayOrder;
                return displayOrderA != null && displayOrderB != null ? displayOrderA - displayOrderB : -1;
            });

            // Handle form with tabs
            if (this._tabs != null) {
                // Store properties names by tab uid to check if there is an error on current tab 
                // or to know which tab we need to switch on to focus editor with validation error
                const propertyNamesByTabUids = buildArrayRecordWithFunc(propertyNames, (propertyName) => this._formItems[propertyName].section?.tabUid);

                // Get the current active tab to check if there is a validation error
                const currentTab = getVOFValue(this._tabs.getActiveTab()?.uid);

                // Check if validation errors in the current tab, else switch to next tab with validation error
                const errorsCurrentTab = currentTab ? propertyNamesByTabUids[currentTab] : [];
                if (errorsCurrentTab?.length) {
                    scrollAndFocusEditor(this._formItems[errorsCurrentTab[0]].editor);
                } else {
                    // Check the next tab uid to switch
                    const nextTabUid = Object.keys(propertyNamesByTabUids)[0];
                    if (!nextTabUid) { return; }

                    // Switch to next tab and scroll/focus the first editor with validation error
                    this._tabs.switchActiveTabUid(nextTabUid);

                    // Need to wait the after show event to have the form rendered in the new active tab
                    this._tabs.afterShowTabEvent.on(_ => {
                        const firstPropertyName = propertyNamesByTabUids[nextTabUid][0];
                        scrollAndFocusEditor(this._formItems[firstPropertyName].editor);
                    });
                }
            } else if (this._accordions != null) { // Handle form with accordions
                // Get the first property name with validation error and its tab uid to check if need to switch active tab
                const firstPropertyName = propertyNames[0];
                const accordionLabel = this._formItems[firstPropertyName].section?.accordionLabel;

                // Get the accordion, open it and focus the editor with validation error
                const accordion = this._accordions.find(a => a.options.label === accordionLabel);
                accordion?.open();
                scrollAndFocusEditor(this._formItems[firstPropertyName].editor);
            }
        }
    }

    /**
     * When a value has changed in a formItem
     * @param propertyConfig propertyConfig to update, give the propertyName to update
     * @param value new value to set
     */
    protected async _onFormItemValueChange(propertyConfig: PropertyConfig, value: anyReallyAny, formItem: FormItem): Promise<void> {
        // Check if dataModel is given, if not: return
        if (!this._dataModel) { this._log?.warn("No DataModel set"); return; }

        if (this.options.formMode.saveMode === GeneratedFormSaveMode.DYNAMIC) {
            this._pushOrUpdateSaveCacheActions({
                propertyName: propertyConfig.propertyName as keyof DataModelType,
                data: this._editedData,
                value
            });
            if (!this.validateForm()) {
                return;
            }

            // Perform all saved cache actions in promises
            const promises = [];
            for (const action of this._saveCacheActions) {
                promises.push(this._dataModel.setPropertyValue(action.propertyName as keyof DataModelType, this._editedData, action.value));
            }
            await Promise.all(promises).catch(showErrorInDialog);

            // Reset saveCacheActions
            this._saveCacheActions = [];

            if (!this.options.disableVisualReturnOnEditItemProperty) {// FIXME temp code before rework of datamodel
                let componentToApplyIsValid: CjsEditor<unknown> | HTMLElement | HTMLElement[] = formItem.editor;
                const editor = formItem.editor;
                const editorInput = formItem.editor.getInput();
                if (editorInput instanceof CjsInputWithButton) {
                    componentToApplyIsValid = editorInput.input.input;
                }
                if (editorInput instanceof CjsInput) {
                    componentToApplyIsValid = editorInput.input;
                }
                if (editor instanceof CjsDateIntervalCalendarEditor) {
                    componentToApplyIsValid = [editor.startDateInput.input, editor.finishDateInput.input];
                }

                const eltArrays: HTMLElement[] = wrapAsArray(componentToApplyIsValid);
                eltArrays.forEach(elt => elt.classList.add("is-valid")); // TODO Upgrade when datamodel has been reworked : issue #74

                setTimeout(() => {
                    eltArrays.forEach(elt => elt.classList.remove("is-valid"));
                }, VISUAL_RETURN_DELAY);
            }

            // refresh formItem
            this._updateFormItemValue(formItem, this._editedData);

            // dispatch change
            this._dispatchOnDataModelValueSaved();
        }

        // Check in create and static, if the current editor is valid, else don't process save
        if (this.options.formMode.editMode === GeneratedFormEditMode.CREATE && this.options.formMode.saveMode === GeneratedFormSaveMode.STATIC) {
            // Check for editor validation
            if (!this.validateForm({ propertyUids: [propertyConfig.propertyName] })) { return; }
        }

        // * We never check for validation in create and dynamic, forbidden and impossible state

        if (this.options.formMode.saveMode === GeneratedFormSaveMode.STATIC) {
            // If static mode, add value update to cache, saving will be perform when save() method will be called
            this._pushOrUpdateSaveCacheActions({
                propertyName: propertyConfig.propertyName as keyof DataModelType,
                data: this._editedData,
                value
            });
        }

    }

    /**
     * Push save action if doesn't exist in the cache actions, else value is updated
     * @param saveAction Contains propertyName, editedData and the value to save
     */
    protected _pushOrUpdateSaveCacheActions(saveAction: GeneratedFormSaveAction<DataModelType>): void {
        const existingSaveAction = this._saveCacheActions.find(sa => sa.propertyName === saveAction.propertyName);

        // Update value if save action already exists
        if (existingSaveAction) {
            existingSaveAction.value = saveAction.value;
        }
        else {
            this._saveCacheActions.push(saveAction);
        }
    }

    /**
     * Update all forms value with a list of DataModelType values
     */
    protected _updateFormItemsValues(editedData: DataModelType[]): void {
        for (const propertyUid in this._formItems) {
            const formItem: FormItem = this._formItems[propertyUid];
            this._updateFormItemValue(formItem, editedData);
        }
    }

    /**
     * Update form item value
     */
    protected _updateFormItemValue(formItem: FormItem, editedData: DataModelType[]): void {

        // Don't display item if hidden is desired behavior
        toggleVisible(formItem.htmlElement, getVOFValue(formItem.propertyConfig.options?.hidden) !== true);

        // Set editors value
        if (!editedData || editedData.length === 0 || editedData[0] === undefined) {
            this.defaultSetEditorsValue(formItem, formItem.editor.getValue() ?? (getVOFValue(formItem.propertyConfig.options?.defaultValue) ?? undefined), true);
            // TODO disable editor. Com(AMT): I'm not sure about this ...
        } else {

            // the value that will be given to the editor is always the value of the first element in the list
            const rawPropertyValue = this.getDefaultRawPropertyValue(formItem.propertyConfig, editedData[0]);

            const propertyValue = rawPropertyValue != null ? rawPropertyValue : getVOFValue(formItem.propertyConfig.options?.defaultValue) ?? undefined;
            let needRefresh = this.defaultSetEditorsValue(formItem, propertyValue, false);

            // we check if we have multiple values
            let multipleValuesFound = false;
            for (let i = 1; i < editedData.length; i++) {
                const otherRawPropertyValue = this.getDefaultRawPropertyValue(formItem.propertyConfig, editedData[i]);
                if (otherRawPropertyValue !== rawPropertyValue) {
                    multipleValuesFound = true;
                    break;
                }
            }
            if (formItem.editor.options.setWithoutRefresh({ multipleValuesState: multipleValuesFound })) {
                needRefresh = true;
            }
            if (needRefresh && formItem.editor.isInitDone) {
                // if !isInitDone refresh will be called later
                formItem.editor.refresh();
            }
        }
    }

    /**
     * Get default raw property
     * Can be overridden
     */
    public getDefaultRawPropertyValue(propertyConfig: PropertyConfig, editedData: DataModelType): anyReallyAny {
        if (editedData == null) { return undefined; }

        const propertyValue = getProp(editedData, propertyConfig.propertyName);
        if (ReferencePropertyConfigTypes.includes(propertyConfig.typeUid) && Array.isArray(propertyValue)) { // It Handles referenceListEditor case
            return [...propertyValue];
        } else {
            return propertyValue;
        }
    }

    /**
     * Set editors value
     * Can be overridden
     */
    public defaultSetEditorsValue(formItem: FormItem, propertyValue: anyReallyAny, refreshIfChanged: boolean): boolean {
        if (formItem.editor instanceof CjsReferenceListEditor) {
            return formItem.editor.setSelectedValues(propertyValue ?? [], refreshIfChanged);
        } else {
            return formItem.editor.setValue(propertyValue, { dispatchEvents: false, refreshIfChanged });
        }
    }

    /**
     * Dispatch event: "A dataModel value has been saved"
     */
    protected _dispatchOnDataModelValueSaved(): void {
        this.dataModelValueSaveEvent.emit();
    }

    /** Hide the form container if necessary */
    protected _refreshHideOnEmptyEditedData(): void {
        // Always visible in create mode
        toggleVisible(this.components.generatedFormContainer, this.options.formMode.editMode === GeneratedFormEditMode.CREATE || (!this.options.disableHideOnEmptyEditedData && this._editedData.length > 0 || this.options.disableHideOnEmptyEditedData), Display.FLEX);
    }

    /** Check if requested form mode is forbidden and refresh if necessary */
    protected _refreshFormMode(): void {
        if (this.options.formMode.editMode === FORBIDDEN_GENERATED_FORM_MODE.editMode && this.options.formMode.saveMode === FORBIDDEN_GENERATED_FORM_MODE.saveMode) {
            this._log?.warn(`[GeneratedForm] The requested form mode is forbidden. Automatically use default mode instead. Default editMode: ${DEFAULT_GENERATED_FORM_MODE.editMode}, default saveMode: ${DEFAULT_GENERATED_FORM_MODE.saveMode}.`);
            this.options.setWithoutRefresh({ formMode: DEFAULT_GENERATED_FORM_MODE });
        }
    }

    //#region Rendering

    /**
     * Render formsItems in component 
     */
    protected _renderFormItems(propertyConfigs: PropertyConfig[]): void {
        this._formItems = {};

        // Contains instance of accordion and list of html element to append into accordion to group items
        const accordionList: AccordionItems = {};
        const tabsItemList: TabsItems = {};

        // Check propertyConfigs propertyNames duplicates, if duplicates found, display warn message in console
        this._checkPropertyConfigsPropertyNameDuplicates();

        // Compute accordionsOptionsList and tabsOptionsList before rendering
        for (const pc of propertyConfigs) {

            // Build group of item with accordion or tabs if needed
            const groupOption = pc.options?.section?.group;
            const tabsOption = pc.options?.section?.tabs;

            // Accordions section
            if (groupOption) {

                let accordionItem = accordionList[groupOption.displayOrder];
                // Add new accordion if not exists
                if (!accordionItem) {
                    // Instantiate new accordion with its options
                    const accordion = new CjsAccordion();
                    // Add toggle event listener to store accordions state (collapsed or opened)   
                    accordion.toggleEvent.on((e) => { this.accordionCollapsedStateMap.set(groupOption.label, !e.detail.open); });

                    const parent = groupOption.parent;
                    let accordionParent = undefined;
                    if (parent) {
                        accordionParent = accordionList[parent.displayOrder].accordion;
                    }
                    // Take the saved accordion state, otherwise collapse according to the options
                    const collapsed = this.accordionCollapsedStateMap.get(groupOption.label) ?? (groupOption.collapsed != null ? groupOption.collapsed : defaultAccordionOptions.collapsed);
                    accordion.options.setWithoutRefresh({
                        label: groupOption.label,
                        collapsed,
                        alwaysOpened: groupOption.alwaysOpened ?? defaultAccordionOptions.alwaysOpened
                    });

                    // Add accordion to accordion list
                    accordionItem = {
                        accordion: accordion,
                        uid: "accordion_" + groupOption.displayOrder,
                        propertyConfigs: [],
                        parent: accordionParent
                    };
                    accordionList[groupOption.displayOrder] = accordionItem;
                }
                accordionItem.propertyConfigs.push(pc);
            }
            // Tabs section
            else if (tabsOption) {
                let tabItem = tabsItemList[tabsOption.displayOrder];
                if (!tabItem) {
                    tabItem = {
                        uid: tabsOption.uid,
                        displayName: tabsOption.label,
                        propertyConfigs: []
                    };
                    tabsItemList[tabsOption.displayOrder] = tabItem;
                }
                tabItem.propertyConfigs.push(pc);
            }
        }

        // Compute in matrix all layout, columns, rows and colSpan
        this._renderLayout(propertyConfigs, tabsItemList, accordionList);

        // Handle Tabs, move matrix rows in tabContent
        if (!isEmptyObject(tabsItemList)) {
            this.components.generatedFormContainer.append(this._renderFormWithTabs(tabsItemList));
        }

        // Handle Tabs, move matrix rows in accordion content
        if (!isEmptyObject(accordionList)) {
            this.components.generatedFormContainer.append(...this._renderFormWithAccordion(accordionList));
        }

        // prepare getFormItem common options
        const formItemOptions: {
            editorOptions: NewEditorOptions,
            generatedFormOptions: GeneratedFormOptions<DataModelType>
        } = {
            editorOptions: {
                dataSourceContext: this.dataSourceContext,
                isCreationForm: this.options.formMode.editMode === GeneratedFormEditMode.CREATE
            },
            generatedFormOptions: this.options
        };
        // Create formItems
        for (const pc of propertyConfigs) {

            const formItem: FormItem | undefined = this._factory.getFormItem(pc, formItemOptions);

            if (formItem) {
                if (!this.options.disableDisplayRequiredAsterisk && pc.validators?.[requiredValidatorName]) {
                    setHTMLElementAttributes(formItem.htmlElement, {
                        "required": "",
                        "aria-required": ""
                    });
                }

                // Add onChangeValue event subscribe
                formItem.editor.changeEvent.on((e) => {
                    this._onFormItemValueChange(pc, e.detail.value, formItem);

                    // Add on_editor_value_has_changed listener
                    this.editorValueChangeEvent.emit({
                        propertyConfig: pc,
                        value: e.detail.value
                    });
                });

                // Add a name attribute to the editor as key for validation
                formItem.editor.setAttribute("name", pc.propertyName);

                // Get matrixItem of propertyConfig
                const matrixItem = this._getRenderMatrixItem(formItem.propertyConfig.propertyName);

                // If colSpan in propertyConfig, remove flexItem of label, and set default width (dev can still override this width if needed)
                if (this._colSpanInPropertyConfigs && this._getColumnsCount() > 1 && this._getLabelLayout(pc) !== "block-level") {
                    const labelDiv = formItem.htmlElement.querySelector(".form-item-label") as HTMLDivElement | undefined;
                    if (labelDiv) {
                        labelDiv.classList.remove(FLEX_ITEM);

                        let width = "80px";// Default
                        // Option from generatedForm (global option)
                        if (this.options.layout?.labelWidth != null) {
                            width = toPx(this.options.layout?.labelWidth);
                        }
                        // Assign option from pc in last
                        if (pc.options?.layout?.labelWidth != null) {
                            width = toPx(pc.options.layout?.labelWidth);
                        }
                        labelDiv.style.width = width;
                    }
                }

                // Append editor in row
                if (matrixItem) {

                    setHTMLElementAttributes(formItem.htmlElement, {
                        col: String(matrixItem.item.col),
                        row: String(matrixItem.item.row),
                        colSpan: String(this._getColSpan(pc))
                    });

                    // Add editor
                    matrixItem.item.html.append(formItem.htmlElement);

                    // Set colSpan of editor row
                    formItem.htmlElement.style.flex = String(pc.options?.layout?.colSpan ?? 1);

                    // Add line-break
                    if (matrixItem.item.appendLineBreak) {
                        // Create an empty form-item if use want to add a line-break after the property
                        matrixItem.item.html.append(createHtmlElement("div", {
                            cssClass: [FLEX_CONTAINER, "form-item", "form-item-filler"],
                            style: {
                                flexGrow: String(this._getColumnsCount() - this._getRowTotalColSpan(matrixItem.rowUid))
                            }
                        }));
                    }
                } else {
                    // Add html form to GeneratedForm component if matrixItem not found
                    this.components.generatedFormContainer.append(formItem.htmlElement);
                }

                // Push item into list
                this._formItems[formItem.propertyUid] = formItem;
            }
        }

        this._updateFormItemsValues(this._editedData);
    }

    /**
     * Render form with accordion components
     *
     * @param accordionList List of accordions to append associated form
     * @memberof CjsGeneratedForm
     */
    protected _renderFormWithAccordion(accordionList: AccordionItems): CjsAccordion[] {
        // Append items to their associated accordion and append accordion to DOM
        this._accordions = [];
        for (const accordionItem of Object.values(accordionList)) {
            const uid: string = accordionItem.uid;
            const accordion = accordionItem.accordion;
            const parent = accordionItem.parent;

            const renderMatrixItems = Object.entries(this._renderMatrix).filter(entry => {
                const itemUid = entry[0];// Get uid key
                return itemUid.substring(0, itemUid.lastIndexOf("_")) === uid;
            }).map(entry => entry[1]).flat();// Return renderMatrixItem and flat double array

            // Append items to accordion
            accordion.onAfterInit = () => {
                setHTMLElementContent(accordion.components.accordionBody, ...renderMatrixItems.map(item => item.html));
                const parentId = parent?.components.accordionBodyContainer.id ?? this.components.generatedFormContainer.id;
                accordion.options.setWithoutRefresh({ parentId });
                if (parent) {
                    // ! Considering the padding given to generated form label (50px), not sure if it's best to give the same padding, or use a class padding
                    accordion.components.accordionItem.classList.add(MARGIN.MS_2);
                    parent.addContent(accordionItem.accordion);
                }
            };
            this._accordions.push(accordion);
        }

        return this._accordions;
    }

    /**
     * Render form with tabs component
     *
     * @param tabsItemList 
     * @memberof CjsGeneratedForm
     */
    protected _renderFormWithTabs(tabsItemList: TabsItems): CjsTabs {
        // Instantiate Tabs component and associate forms to tabs with uid 
        this._tabs = new CjsTabs();
        const tabList: TabElement[] = [];

        // Build tab names list to initialize tabs component
        Object.keys(tabsItemList).forEach(order => {
            const displayOrder = Number(order);
            const uid: string = tabsItemList[displayOrder].uid;

            const renderMatrixItems = Object.entries(this._renderMatrix).filter(entry => {
                const itemUid = entry[0];// Get uid key
                return itemUid.substring(0, itemUid.lastIndexOf("_")) === uid;
            }).map(entry => entry[1]).flat();// Return renderMatrixItem and flat double array

            // Append each for item in associated tab by tab uid
            const divContainer = createHtmlElement("div", {
                innerHTML: renderMatrixItems.map(item => item.html)
            });

            tabList.push({
                uid,
                label: tabsItemList[displayOrder].displayName,
                tabContent: {
                    content: divContainer
                }
            });
        });
        // Initialize tabs component with tab names list
        this._tabs.setData(tabList, false);
        return this._tabs;
    }


    /** 
     * Get tabs of generated form or undefined if no tab section has been set-up in the property configs
     * @returns {*} {CjsTabs | undefined} 
     * */
    public getTabs(): CjsTabs | undefined {
        return this._tabs;
    }

    /** Change the layout of the form and refresh */
    protected _renderLayout(propertyConfigs: PropertyConfig[], tabsItemList: TabsItems, accordionList: AccordionItems): void {

        // Empty forms before render
        empty(this.components.generatedFormContainer);

        // Reset values
        this._renderMatrix = {};
        this._colSpanInPropertyConfigs = false;

        // Get columnsCount
        const columnsCount = this._getColumnsCount();

        // Don't render if columnsCount given if 0 or inf value
        if (columnsCount && columnsCount <= 0) {
            return;
        }

        // PropertyConfigs to not render in Accordions or tabs
        const pcToRender = [...propertyConfigs];

        // Render tabsMatrix
        if (!isEmptyObject(tabsItemList)) {
            for (const tabItem of Object.values(tabsItemList)) {
                this._computeMultipleColumnsMatrix({ propertyConfigs: tabItem.propertyConfigs, columnsCount, rowUidPrefix: tabItem.uid + "_" });
                removeElementsFromArray(pcToRender, tabItem.propertyConfigs, propertyConfigComparator);
            }
        }

        // Render accoridon matrix
        if (!isEmptyObject(accordionList)) {
            for (const accordionItem of Object.values(accordionList)) {
                this._computeMultipleColumnsMatrix({ propertyConfigs: accordionItem.propertyConfigs, columnsCount, rowUidPrefix: accordionItem.uid + "_" });
                removeElementsFromArray(pcToRender, accordionItem.propertyConfigs, propertyConfigComparator);
            }
        }

        // Render classic propertyConfigs
        this._computeMultipleColumnsMatrix({ propertyConfigs: pcToRender, columnsCount, rowUidPrefix: ROW_UID + "_" });

    }


    /** Compute rows and columns for given propertyConfigs */
    protected _computeMultipleColumnsMatrix(p: {
        propertyConfigs: PropertyConfig[],
        columnsCount: number,
        rowUidPrefix: string
    }): void {

        // Store count of columns filled for currentRow
        let colsFilledCount = 0;

        // Store current row where editors will be added
        let currentRow: HTMLDivElement | undefined;

        // Store row count
        let rowCount = 0;
        let previousLineBreak = false;

        for (const pc of p.propertyConfigs) {
            const colSpan = this._getColSpan(pc);

            // If no more col space available, add item to next row (or a line-break is desired)
            if (colsFilledCount + colSpan > p.columnsCount || previousLineBreak) {
                colsFilledCount = colSpan;
                rowCount++;
            } else {// Increase colFilledCount if still space available
                colsFilledCount += colSpan;
            }

            // Get rowUid
            const rowUid = p.rowUidPrefix + rowCount;
            // Get renderMatrixItems for currentRow
            let rowItems = this._renderMatrix[rowUid];


            // Create item if needed
            if (!rowItems) {
                rowItems = [];
                this._renderMatrix[rowUid] = rowItems;

                currentRow = createHtmlElement("div", {
                    cssClass: [FLEX_CONTAINER, "form-row"],
                    attributes: {
                        rowUid: String(rowCount)
                    }
                });

                // Add row to generatedFormContainer
                // Note: row will be moved if item is in a Tab or an Accordion
                this.components.generatedFormContainer.append(currentRow);
            }

            // If colSpan given, set attribute to true
            if (this._getColSpanFromOptions(pc) != null) {
                this._colSpanInPropertyConfigs = true;
            }
            previousLineBreak = this._getAppendLineBreak(pc);

            // Add item to renderMatrixItems of current row
            if (currentRow) {
                addToArrayRecord(this._renderMatrix, rowUid, {
                    col: rowItems.length + 1,
                    row: rowCount + 1,
                    propertyConfig: pc,
                    html: currentRow,
                    appendLineBreak: previousLineBreak
                });
            }

        }
    }

    /** Get renderMatrix item for a propertyConfig */
    protected _getRenderMatrixItem(propertyName: string): { item: GeneratedFormRenderFormGridItem, rowUid: string } | undefined {

        for (const [rowUid, matrixItems] of Object.entries(this._renderMatrix)) {
            const matrixItem = matrixItems.find(item => item.propertyConfig.propertyName === propertyName);
            if (matrixItem) {
                return { item: matrixItem, rowUid };
            }
        }

        return undefined;
    }

    /** Get columns count of generatedForm */
    protected _getColumnsCount(): number {
        return this.options.layout?.columnsCount ?? 1;// if not given, one column
    }

    /** Get col span of a propertyConfig */
    protected _getColSpanFromOptions(propertyConfig: PropertyConfig): number | undefined {
        const pcLayoutInOptions = this.options.layout?.propertyConfigsLayout?.find(item => item.propertyName === propertyConfig.propertyName);
        const pc = this._propertyConfigs?.find(propConfig => propertyConfigComparator(propConfig, propertyConfig));

        // Priority for colSpan: generatedFormOption: "propertyConfigsLayout" > propertyConfig layout option > 1
        return pcLayoutInOptions?.colSpan ?? pc?.options?.layout?.colSpan;
    }


    /** Get col span of a propertyConfig */
    protected _getColSpan(propertyConfig: PropertyConfig): number {
        // If colSpan not given, set to 1
        let colSpan = this._getColSpanFromOptions(propertyConfig) ?? 1;
        const colsCount = this._getColumnsCount();

        // Check if colSpan if upper than columnCount => reduce colSpan to columnCount value
        if (colSpan > colsCount) { colSpan = colsCount; }

        return colSpan;
    }

    /** Get appendLineBreak of a propertyConfig */
    protected _getAppendLineBreak(propertyConfig: PropertyConfig): boolean {
        const pcLayoutInOptions = this.options.layout?.propertyConfigsLayout?.find(item => item.propertyName === propertyConfig.propertyName);
        const pc = this._propertyConfigs?.find(propConfig => propertyConfigComparator(propConfig, propertyConfig));

        let appendLineBreak = Boolean(pc?.options?.layout?.appendLineBreak);
        if (pcLayoutInOptions?.appendLineBreak != null) {// generatedFormOption: "propertyConfigsLayout" 
            appendLineBreak = pcLayoutInOptions.appendLineBreak;
        }

        return appendLineBreak;
    }
    /** Get labelLayout of a propertyConfig */
    protected _getLabelLayout(propertyConfig: PropertyConfig): PropertyLabelLayout {
        const pc = this._propertyConfigs?.find(propConfig => propertyConfigComparator(propConfig, propertyConfig));

        let labelLayout = this.options.layout?.formLabelLayout;
        const pcLabelLayout = pc?.options?.layout?.labelLayout;
        if (pcLabelLayout != null) {// Override with params in pc
            labelLayout = pcLabelLayout;
        }

        return labelLayout ?? "inline-level";
    }

    /** Get total col span of editors in a single row*/
    protected _getRowTotalColSpan(rowUid: string): number {
        const matrixItems = this._renderMatrix[rowUid];
        // Sum all values of colSpans of editors in this row
        const totalColSpan = matrixItems.map(item => item.propertyConfig.options?.layout?.colSpan ?? 1).reduce((pv, cv) => pv + cv);
        return totalColSpan !== 0 ? totalColSpan : 1;// Ensure if no editors found, return 1 
    }


    //#endregion Rendering

    /** Check propertyConfigs list propertyName duplicates */
    protected _checkPropertyConfigsPropertyNameDuplicates(): void {
        const propertyNames = (this._propertyConfigs ?? []).map(pc => pc.propertyName);

        if (hasDuplicates(propertyNames)) {
            getLogConsole().warn(`[CjsGeneratedForm] - List of given propertyConfigs has propertyNames duplicates: ${getDuplicates(propertyNames).join(", ")}`);
        }
    }
}
